import { type Ref, readonly, ref } from 'vue';
import { useRouter } from 'vue-router';

import type { PlatformStartImpersonationInput } from '@shared/api/graphql/gql/graphql';

import { fetchAuthContext } from '@/entities/auth';
import {
  useEnterOrganizationAsOwnerMutation,
  useStartPlatformImpersonationMutation,
  useStopPlatformImpersonationMutation,
} from '@/entities/platform';
import { apolloOperationGate } from '@/shared/api';
import {
  navigateToApplicationUrl,
  resolveAppImpersonationHandoffUrl,
  resolvePlatformHomeUrl,
  useLocationStorage,
  useOrganizationStorage,
} from '@/shared/config';
import { getGraphQLErrorCode } from '@/shared/lib';
import { useToast } from '@/shared/lib/toast';

const START_ERROR_MESSAGE = 'Не удалось открыть организацию. Повторите попытку.';
const STOP_ERROR_MESSAGE = 'Не удалось завершить вход от имени пользователя. Повторите попытку.';
const CONFIGURATION_ERROR_MESSAGE =
  'Не удалось определить адрес приложения. Обновите страницу или обратитесь к администратору.';
const ACTIVE_SESSION_ERROR_MESSAGE =
  'У вас уже есть активная сессия входа. Завершите её в журнале и повторите попытку.';
const ACTIVE_SESSION_ERROR_CODE = 'IMPERSONATION_SESSION_ALREADY_ACTIVE';
const OWNER_MISSING_ERROR_MESSAGE = 'В организации нет работающего владельца';
const OWNER_MISSING_ERROR_CODE = 'ORGANIZATION_OWNER_MISSING';
const SESSION_TRANSITION_LOG_PREFIX = '[FIX:impersonation-session-transition]';

type TransitionOperation = 'start' | 'enter' | 'stop';

const errorClass = (error: unknown): string => (error instanceof Error ? error.name : 'UnknownError');

const logSessionTransition = (
  operation: TransitionOperation,
  phase: 'queued' | 'started' | 'completed',
  context: Record<string, boolean> = {},
): void => {
  // eslint-disable-next-line no-console
  console.debug(SESSION_TRANSITION_LOG_PREFIX, { operation, phase, ...context });
};

const warnSessionTransition = (
  operation: TransitionOperation,
  phase: 'reconciling' | 'failed',
  reasonCode: 'mutation_failed' | 'server_state_not_confirmed',
  error?: unknown,
): void => {
  console.warn(SESSION_TRANSITION_LOG_PREFIX, {
    operation,
    phase,
    reason_code: reasonCode,
    ...(error === undefined ? {} : { exception_class: errorClass(error) }),
  });
};

/** Жёсткий переход после подтверждённой смены сессии; сбой только логируется, ошибку показывает вызывающий. */
const navigateAfterTransition = (operation: TransitionOperation, destination: URL): boolean => {
  try {
    navigateToApplicationUrl(destination);
    logSessionTransition(operation, 'completed');
    return true;
  } catch (error: unknown) {
    console.error(SESSION_TRANSITION_LOG_PREFIX, {
      operation,
      phase: 'failed',
      reason_code: 'navigation_failed',
      exception_class: errorClass(error),
    });
    return false;
  }
};

/** Активна ли impersonation на сервере и совпадает ли она с ожидаемой организацией (и целью, если задана). */
const reconcileActiveSession = async (expected: {
  organizationId?: string | null;
  targetUserId?: string;
}): Promise<{ active: boolean; matches: boolean }> => {
  const result = await fetchAuthContext();

  if (result.status === 'failure' || result.status === 'unauthenticated') {
    return { active: false, matches: false };
  }

  const { impersonation } = result.context;

  return {
    active: impersonation.active,
    matches:
      impersonation.active &&
      impersonation.organization?.id === expected.organizationId &&
      (expected.targetUserId === undefined || impersonation.target_user?.id === expected.targetUserId),
  };
};

const reconcileStop = async (): Promise<boolean> => {
  const result = await fetchAuthContext();

  if (result.status === 'failure' || result.status === 'unauthenticated') {
    return false;
  }

  return Boolean(result.context.user?.is_super_user) && !result.context.impersonation.active;
};

export const useImpersonation = () => {
  const router = useRouter();
  const toast = useToast();
  const { clearLocationId } = useLocationStorage();
  const { resetOrganizationId } = useOrganizationStorage();
  const { mutate: startMutation } = useStartPlatformImpersonationMutation();
  const { mutate: stopMutation } = useStopPlatformImpersonationMutation();
  const { mutate: enterMutation } = useEnterOrganizationAsOwnerMutation();
  const isStarting = ref(false);
  const isStopping = ref(false);

  /**
   * Общий каркас смены сессии: адрес назначения, защита от повтора и сериализация
   * с обновлением данных. `transition` подтверждает состояние сервера и уводит на `destination`.
   */
  const runTransition = async (
    operation: TransitionOperation,
    busy: Ref<boolean>,
    resolveDestination: () => URL,
    transition: (destination: URL) => Promise<boolean>,
    queuedContext?: Record<string, boolean>,
  ): Promise<boolean> => {
    if (busy.value) {
      return false;
    }

    let destination: URL;
    try {
      destination = resolveDestination();
    } catch {
      toast.error(CONFIGURATION_ERROR_MESSAGE);
      return false;
    }

    busy.value = true;
    logSessionTransition(operation, 'queued', queuedContext);

    try {
      return await apolloOperationGate.runExclusive('tenant-transition', async () => {
        logSessionTransition(operation, 'started');
        return transition(destination);
      });
    } finally {
      busy.value = false;
    }
  };

  /**
   * Откат начатой на сервере сессии, когда клиент не смог её подтвердить.
   * Сбой компенсации только логируется: пользователю уже показана ошибка старта.
   */
  const compensateStart = async (): Promise<void> => {
    try {
      const compensation = await stopMutation();

      if (compensation?.data?.platformStopImpersonation.active !== false) {
        throw new Error('Compensation response is not inactive');
      }
    } catch (error: unknown) {
      console.error('[impersonation.start] Compensation failed', {
        reason_code: 'start_compensation_failed',
        exception_class: errorClass(error),
      });
    }
  };

  const start = async (input: PlatformStartImpersonationInput): Promise<boolean> => {
    const { organizationId } = input;

    if (!organizationId) {
      toast.error(START_ERROR_MESSAGE);
      return false;
    }

    return runTransition(
      'start',
      isStarting,
      () => resolveAppImpersonationHandoffUrl(organizationId),
      async (destination) => {
        let startState:
          | {
              active: boolean;
              target_user?: { id: string } | null;
              organization?: { id: string } | null;
            }
          | null
          | undefined;
        let startError: unknown;

        try {
          const result = await startMutation({ input });
          startState = result?.data?.platformStartImpersonation;
        } catch (error: unknown) {
          startError = error;
          warnSessionTransition('start', 'reconciling', 'mutation_failed', error);
        }

        if (getGraphQLErrorCode(startError) === ACTIVE_SESSION_ERROR_CODE) {
          toast.error(ACTIVE_SESSION_ERROR_MESSAGE);
          await router.push({ name: 'platform.audit', query: { status: 'active' } });

          return false;
        }

        let serverActive = startState?.active === true;
        let startConfirmed =
          serverActive &&
          startState?.target_user?.id === input.targetUserId &&
          startState.organization?.id === organizationId;

        if (!startConfirmed) {
          const reconciled = await reconcileActiveSession(input);
          serverActive = serverActive || reconciled.active;
          startConfirmed = reconciled.matches;
        }

        if (!startConfirmed) {
          if (serverActive) {
            await compensateStart();
          }

          warnSessionTransition('start', 'failed', 'server_state_not_confirmed');
          toast.error(START_ERROR_MESSAGE);
          return false;
        }

        if (navigateAfterTransition('start', destination)) {
          return true;
        }

        await compensateStart();
        toast.error(START_ERROR_MESSAGE);
        return false;
      },
      { has_organization_id: true, has_target_user_id: Boolean(input.targetUserId) },
    );
  };

  /**
   * Вход суперюзера в чужую организацию под её владельцем, в том числе
   * из активной impersonation: сервер закрывает прежнюю сессию сам.
   */
  const enterOrganization = async (organizationId: string): Promise<boolean> =>
    runTransition(
      'enter',
      isStarting,
      () => resolveAppImpersonationHandoffUrl(organizationId),
      async (destination) => {
        let enterConfirmed = false;

        try {
          const result = await enterMutation({ organizationId });
          const state = result?.data?.enterOrganizationAsOwner;
          enterConfirmed = state?.active === true && state.organization?.id === organizationId;
        } catch (error: unknown) {
          warnSessionTransition('enter', 'reconciling', 'mutation_failed', error);

          if (getGraphQLErrorCode(error) === OWNER_MISSING_ERROR_CODE) {
            toast.error(OWNER_MISSING_ERROR_MESSAGE);
            return false;
          }
        }

        if (!enterConfirmed) {
          enterConfirmed = (await reconcileActiveSession({ organizationId })).matches;
        }

        if (!enterConfirmed) {
          warnSessionTransition('enter', 'failed', 'server_state_not_confirmed');
          toast.error(START_ERROR_MESSAGE);
          return false;
        }

        if (navigateAfterTransition('enter', destination)) {
          return true;
        }

        toast.error(START_ERROR_MESSAGE);
        return false;
      },
    );

  /** Без `organizationId` возвращает в HQ, с ним — в эту организацию через handoff. */
  const stop = async (options: { organizationId?: string } = {}): Promise<boolean> =>
    runTransition(
      'stop',
      isStopping,
      () =>
        options.organizationId ? resolveAppImpersonationHandoffUrl(options.organizationId) : resolvePlatformHomeUrl(),
      async (destination) => {
        let stopConfirmed = false;

        try {
          const result = await stopMutation();
          stopConfirmed = result?.data?.platformStopImpersonation.active === false;
        } catch (error: unknown) {
          warnSessionTransition('stop', 'reconciling', 'mutation_failed', error);
        }

        if (!stopConfirmed) {
          stopConfirmed = await reconcileStop();
        }

        if (!stopConfirmed) {
          warnSessionTransition('stop', 'failed', 'server_state_not_confirmed');
          toast.error(STOP_ERROR_MESSAGE);
          return false;
        }

        resetOrganizationId();
        clearLocationId();

        if (navigateAfterTransition('stop', destination)) {
          return true;
        }

        toast.error(STOP_ERROR_MESSAGE);
        return false;
      },
      { has_organization_id: Boolean(options.organizationId) },
    );

  return {
    isStarting: readonly(isStarting),
    isStopping: readonly(isStopping),
    enterOrganization,
    start,
    stop,
  };
};
